import { useState, useEffect } from 'react';
import { X, Calendar, ArrowRight, AlertCircle, Loader2 } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';

interface CompromiseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onValidate: (compromiseDate: string, expectedActeDate: string) => Promise<void>;
  disabled?: boolean;
}

export default function CompromiseModal({ 
  isOpen, 
  onClose, 
  onValidate,
  disabled = false 
}: CompromiseModalProps) {
  const [compromiseDate, setCompromiseDate] = useState('');
  const [expectedActeDate, setExpectedActeDate] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('[CompromiseModal] Modal opened, resetting form');
      setCompromiseDate('');
      setExpectedActeDate('');
      setError('');
      setSubmitting(false);
    }
  }, [isOpen]);

  // Handle escape key
  useEscapeKey(() => {
    if (isOpen && !submitting && !disabled) {
      onClose();
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('[CompromiseModal] Form submitted');
    console.log('[CompromiseModal] Current state:', { compromiseDate, expectedActeDate, submitting, disabled });
    
    if (submitting || disabled) {
      console.log('[CompromiseModal] Form submission blocked - submitting or disabled');
      return;
    }

    // Validation
    if (!compromiseDate || !expectedActeDate) {
      console.log('[CompromiseModal] Validation failed - missing dates');
      setError('Tous les champs sont obligatoires');
      return;
    }

    // Validate that expected acte date is after compromise date
    const compromiseDateObj = new Date(compromiseDate);
    const expectedActeDateObj = new Date(expectedActeDate);
    
    console.log('[CompromiseModal] Date validation:', { compromiseDateObj, expectedActeDateObj });
    
    if (expectedActeDateObj <= compromiseDateObj) {
      console.log('[CompromiseModal] Date validation failed');
      setError('La date prévisionnelle de l\'acte doit être postérieure à la date du compromis');
      return;
    }

    try {
      console.log('[CompromiseModal] Starting validation process');
      setSubmitting(true);
      setError('');
      
      await onValidate(compromiseDate, expectedActeDate);
      
      console.log('[CompromiseModal] Validation successful, closing modal');
      // Close modal immediately without resetting form first
      onClose();
      
    } catch (err: any) {
      console.error('[CompromiseModal] Error in validation:', err);
      setError(err.message || 'Une erreur est survenue');
    } finally {
      console.log('[CompromiseModal] Setting submitting to false');
      setSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <form
        onSubmit={handleSubmit}
        className="bg-white w-full max-w-md rounded-2xl flex flex-col overflow-hidden shadow-2xl"
      >
        {/* ── Header ── */}
        <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-6 py-5">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                <Calendar size={20} className="text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-white">Passage en compromis</h2>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full font-medium">Potentiel</span>
                  <ArrowRight size={10} className="text-slate-400" />
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">Compromis</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting || disabled}
              className="w-9 h-9 rounded-xl bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors shrink-0 disabled:opacity-50"
            >
              <X size={18} className="text-white" />
            </button>
          </div>
        </div>

        {/* ── Content ── */}
        <div className="p-6 space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Date du compromis
            </label>
            <div className="relative">
              <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="date"
                value={compromiseDate}
                onChange={(e) => setCompromiseDate(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400 disabled:opacity-50 transition-colors"
                required
                disabled={submitting || disabled}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Date prévisionnelle de l'acte
            </label>
            <div className="relative">
              <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="date"
                value={expectedActeDate}
                onChange={(e) => setExpectedActeDate(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400 disabled:opacity-50 transition-colors"
                required
                disabled={submitting || disabled}
              />
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">
              <AlertCircle size={14} className="shrink-0" />
              {error}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="border-t border-slate-100 px-6 py-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting || disabled}
            className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 disabled:opacity-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={submitting || disabled || !compromiseDate || !expectedActeDate}
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-amber-500 rounded-xl hover:bg-amber-600 disabled:opacity-50 transition-all shadow-sm"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {submitting ? 'Validation…' : 'Valider'}
          </button>
        </div>
      </form>
    </div>
  );
}